import type { DeepPartial, Middleware, Reducer, StoreEnhancer } from '@reduxjs/toolkit';
import { createStoreWithMiddleware, excludeReducerNetworkStateData } from 'c-data-layer';
import { sessionCheckReducer as sessionCheck } from 'c-auth-module/Slices/SessionCheck/session-check-slice';
import { authReducer as auth } from 'c-auth-module/Slices/Auth/auth-slice';
import { passwordResetReducer as passwordReset } from 'c-auth-module/Slices/PasswordReset/password-reset-slice';
import { uiReducer as ui } from 'c-wrapper/Slices/UI';
import type { CommonState } from 'c-types';
import type { PersistConfig } from 'redux-persist';
import { excludeProperties } from 'c-lib';
import { initSentry } from './sentry';
// import { reducerPersistTransformer as noteReducerPersistTransformer } from 'c-notes-module/Lib';

const commonEnhancers: StoreEnhancer[] = [];

const commonTransforms = [
    excludeReducerNetworkStateData({
        loadingById: true,
        updatingById: true,
        deletingById: true,
        create: true,
        newestCreatedId: true,
        pagination: true,
    }),
    // noteReducerPersistTransformer,
];
/**
 * The only slices written to localStorage: the session and UI preferences, enough for a reload of
 * a logged-in page. Entity slices hold client and campaign data, which mustn't outlive the session
 * on a shared machine; pages fetch them again after a reload anyway.
 */
const persistedSlices: Extract<keyof CommonState, string>[] = ['auth', 'ui'];

const commonReducers = {
    auth,
    sessionCheck,
    passwordReset,
    ui,
};

if (import.meta.env.VITE_SENTRY_DSN) {
    commonEnhancers.push(
        initSentry({
            dsn: import.meta.env.VITE_SENTRY_DSN,
            environment: import.meta.env.VITE_SENTRY_ENV,
        }),
    );
}

type CreateStoreOptions<RootState extends CommonState> = {
    version: string;
    reducers?: { [reducerName in keyof Partial<Omit<RootState, keyof CommonState>>]: Reducer };
    excludeReducers?: (keyof RootState)[];
    transforms?: PersistConfig<RootState>['transforms'];
    enhancers?: StoreEnhancer[];

    preloadedState?: DeepPartial<RootState>;
    middleware?: Middleware[];
};

const createStore = <RootState extends CommonState>({
    version,
    reducers,
    excludeReducers,
    transforms,
    enhancers,
    ...options
}: CreateStoreOptions<RootState>) => {
    if (localStorage) {
        // 'persist:root-' is the prefix given to the local storage keys by `redux-persist`
        const reduxPersistPrefix = 'persist:root-';
        Object.keys(localStorage)
            .filter(key => key.startsWith(reduxPersistPrefix))
            .map(key => key.replace(reduxPersistPrefix, ''))
            .filter(key => key !== version)
            .forEach(key => localStorage.removeItem(`${reduxPersistPrefix}${key}`));
    }

    return createStoreWithMiddleware<RootState>({
        version,
        transforms: [...commonTransforms, ...(transforms ?? [])],
        reducers: excludeProperties({ ...reducers, ...commonReducers } as any, excludeReducers),
        whitelist: persistedSlices,
        enhancers: [...commonEnhancers, ...(enhancers ?? [])],
        ...options,
    });
};
export default createStore;
